import React, { FC, useState, ReactElement } from "react";
import "./tally.scss";
import logoIDProtect from "assets/img/logo_ID-Protect-blanc-icon.png";
import { accompanimentSendMail } from "features/tally";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as IconUpload } from "assets/img/icons/icon_Upload.svg";
import Lottie from "lottie-react";
import iconSuccess from "assets/lottie/Success.json";
import { TypeAnimation } from "react-type-animation";



// Types pour les données du formulaire
interface FormData {
  firstName: string;
  lastName: string;
  address: string;
  phone: string;
  incidentDate: string;
  comment: string;
}

interface FileUpload {
  file: File | null;
  preview?: string;
}

const TallyForm: FC = () => {
  const [step, setStep] = useState<1 | 2>(1);

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    address: "",
    phone: "",
    incidentDate: "",
    comment: ""
  });

  const [errorModal, setErrorModal] = useState<{ visible: boolean; message?: string }>({
    visible: false,
    message: "",
  });

  const [identityDocument, setIdentityDocument] = useState<FileUpload>({ file: null });
  const [policeReport, setPoliceReport] = useState<FileUpload>({ file: null });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Gestion des changements dans les champs de texte
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur si l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Validation des types de fichiers
  const validateFile = (file: File): boolean => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10 Mo

    if (!allowedTypes.includes(file.type)) {
      return false;
    }
    
    if (file.size > maxSize) {
      return false;
    }
    
    return true;
  };

  // Gestion de l'upload de fichiers
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'identity' | 'police') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) {
      setErrors(prev => ({
        ...prev,
        [type]: "Fichier invalide. Formats acceptés: .jpeg, .jpg, .png, .pdf. Taille max: 10 Mo"
      }));
      return;
    }

    const fileUpload: FileUpload = { file };
    
    if (type === 'identity') {
      setIdentityDocument(fileUpload);
      setErrors(prev => ({ ...prev, identity: "" }));
    } else {
      setPoliceReport(fileUpload);
      setErrors(prev => ({ ...prev, police: "" }));
    }
  };

  // Validation du formulaire
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Le prénom est obligatoire";
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Le nom est obligatoire";
    }
    
    if (!formData.address.trim()) {
      newErrors.address = "L'adresse est obligatoire";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Le numéro de téléphone est obligatoire";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string,string> = {};
    if (!formData.firstName?.trim()) newErrors.firstName = "Le prénom est obligatoire";
    if (!formData.lastName?.trim())  newErrors.lastName  = "Le nom est obligatoire";
    if (!formData.address?.trim())   newErrors.address   = "L'adresse est obligatoire";
    if (!formData.phone?.trim())     newErrors.phone     = "Le numéro de téléphone est obligatoire";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        firstName: formData.firstName,
        lastName:  formData.lastName,
        address:   formData.address,
        phone:     formData.phone,
        incidentDate: formData.incidentDate || undefined,
        comment:      formData.comment || undefined,
        identityDocument: identityDocument.file || undefined,
        policeReport:     policeReport.file || undefined,
      };

      const { success, message } = await accompanimentSendMail(payload);
      if (!success) throw new Error(message || "Échec de l'envoi");

      setStep(2);

      // alert(message || "Formulaire envoyé !");
      setFormData({ firstName:"", lastName:"", address:"", phone:"", incidentDate:"", comment:"" });
      setIdentityDocument({ file: null });
      setPoliceReport({ file: null });
      setErrors({});
    } catch (err: any) {
      /*alert(err?.message || "Erreur lors de l'envoi");*/
      setErrorModal({
        visible: true,
        message: err?.message || "Une erreur est survenue lors de l’envoi du formulaire. Veuillez réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const variants: any = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
    exit: { opacity: 0, y: -15, transition: { duration: 0.4, ease: "easeIn" } }
  };

  return (
    <div className="form-container">
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="form-step"
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* En-tête */}
            <div className="form-header">

              <div className="logo">
                <img src={logoIDProtect} alt="ID Protect" className="logo-img" loading="lazy" />
                <span className="logo-text">ID PROTECT</span>
              </div>


              <h1 className="form-title">Accompagnement usurpation d'identité</h1>
              <p className="form-subtitle">L'EXPERTISE D'ID PROTECT POUR FAIRE CESSER L'USURPATION D'IDENTITÉ</p>
            </div>

            {/* Section d'information */}
            <div className="info-section">
              <h2>Nous revenons vers vous suite à la souscription de notre offre d'accompagnement</h2>
              <p>
                Nous allons mettre en opposition votre document d'identité chez les 5 organismes que nous
                considérons comme étant les plus fraudés.
              </p>
              <p>
                A compter de la date de réception des courriers, ces organismes de crédits ne pourront pas
                accepter l'ancien document d'identité. En cas d'erreur de leur part, ils ne pourront pas
                non plus vous tenir pour responsable.
              </p>

              <div className="requirements-box">
                <h3>Pour cela, nous avons besoin des éléments suivants</h3>
                <ul>
                  <li>une copie du document compromis auquel il faut faire opposition.</li>
                  <li>une copie du procès-verbal reçu suite à votre dépôt de main courante</li>
                </ul>
                <p>Afin que les organismes puissent vous contacter au besoin :</p>
                <ul>
                  <li>votre adresse actuelle</li>
                  <li>votre numéro de téléphone</li>
                </ul>
              </div>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="form-content">
              {/* Section coordonnées */}
              <div className="form-section">
                <h3>Vos coordonnées</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">Prénom(s)</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={errors.firstName ? "error" : ""}
                    />
                    {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Nom</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={errors.lastName ? "error" : ""}
                    />
                    {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Adresse postale</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className={errors.address ? "error" : ""}
                  />
                  {errors.address && <span className="error-message">{errors.address}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Numéro de téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? "error" : ""}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>

              {/* Section pièce d'identité */}
              <div className="form-section">
                <h3>Pièce(s) identité concernée(s)</h3>

                <div className="form-group">
                  <label>Photo ou copie recto-verso du/des document(s) concerné(s)</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="identityDocument"
                      accept=".jpeg,.jpg,.png,.pdf"
                      onChange={(e) => handleFileUpload(e, 'identity')}
                      className="file-input"
                    />
                    <label htmlFor="identityDocument" className="file-upload-label">
                      <div className="file-upload-content">
                        {identityDocument.file ? (
                          <span className="file-selected">✓ {identityDocument.file.name}</span>
                        ) : (
                          <>
                            <IconUpload fill="#1956A8" className={'file-upload-icon'} width={25}/>
                            <span>Cliquez pour choisir un fichier ou faites-le glisser ici</span>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                  <p className="file-info">Accepte les fichiers .jpeg, .jpg, .png, .pdf</p>
                  <p className="file-info">Limite de taille : 10 Mo</p>
                  {errors.identity && <span className="error-message">{errors.identity}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="incidentDate">Date estimée de la perte ou du vol</label>
                  <input
                    type="date"
                    id="incidentDate"
                    name="incidentDate"
                    value={formData.incidentDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Section procès-verbal */}
              <div className="form-section">
                <h3>Procès verbal ou dépôt de plainte</h3>

                <div className="form-group">
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="policeReport"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => handleFileUpload(e, 'police')}
                      className="file-input"
                    />
                    <label htmlFor="policeReport" className="file-upload-label">
                      <div className="file-upload-content">
                        {policeReport.file ? (
                          <span className="file-selected">✓ {policeReport.file.name}</span>
                        ) : (
                          <>
                            <IconUpload fill="#1956A8" className={'file-upload-icon'} width={25}/>
                            <span>Cliquez pour choisir un fichier ou faites-le glisser ici</span>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                  <p className="file-info">Accepte les fichiers .jpg, .jpeg, .png, .pdf</p>
                  <p className="file-info">Limite de taille : 10 Mo</p>
                  {errors.police && <span className="error-message">{errors.police}</span>}
                </div>
              </div>

              {/* Section commentaire */}
              <div className="form-section">
                <h3>Commentaire</h3>
                <div className="form-group">
            <textarea
              name="comment"
              value={formData.comment}
              onChange={handleInputChange}
              rows={4}
              placeholder="Ajoutez vos commentaires ici..."
            />
                </div>
              </div>

              {/* Bouton de soumission */}
              <div className="form-submit">
                <button type="submit" disabled={loading} className="submit-button">
                  {loading ? "Envoi en cours..." : "Envoyer"}
                </button>
              </div>
            </form>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div
            key="success-step"
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="success-message"
          >
            <div className="success-card">
              <div className="success-content">
                {/* Lottie success animation */}
                <div className="success-animation">
                  <Lottie
                    animationData={iconSuccess}
                    loop={false}
                    autoplay
                    style={{ width: 120, height: 120 }}
                  />
                </div>

                {/* Text area */}
                <div className="success-text">
                  <h2 className="success-title">Formulaire envoyé avec succès</h2>
                  <p className="success-description">
                    Merci <b>{formData.firstName}</b> ! Votre demande a bien été transmise à notre équipe.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

      </AnimatePresence>


      {/* Section prochaines étapes */}
      <div className="next-steps-section">
        {step === 2 ? (
          <h3>
            <TypeAnimation
              sequence={[
                "Prochaines étapes",
                1000,
              ]}
              speed={50}
              repeat={Infinity}
              cursor={false}
              style={{ color: "#1956A8", fontWeight: "600" }}
            />
          </h3>
        ) : (
          <h3>Prochaines étapes</h3>
        )}
        <p>
          Lorsque nous recevrons l'intégralité des accusés réception, nous vous enverrons un mail 
          pour vous en informer et vous donner la date certaine à partir de laquelle votre document 
          est protégé chez chacun des établissements.
        </p>
      </div>

      {/* Error Modal */}
      {errorModal.visible && (
        <motion.div
          className="error-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="error-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <div className="error-icon">
              <Lottie
                animationData={require("assets/lottie/WarningAlert.json")}
                loop={false}
                autoplay
                style={{ width: 100, height: 100 }}
              />
            </div>
            <h2>Échec de l'envoi</h2>
            <p>{errorModal.message}</p>
            <button className="close-button" onClick={() => setErrorModal({ visible: false })}>
              Fermer
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default TallyForm;